import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { reviewAPI, feedbackAPI } from '../services/api';
import { CommentSection } from '../components/CommentSection';
import {
  ChevronLeft,
  Search,
  HelpCircle,
  Bell,
  Mail,
  ChevronDown,
  Clock,
  AlertCircle,
  AtSign,
  MoreVertical,
  UserPlus,
  Reply,
  Copy,
  Paperclip,
  ThumbsUp,
  ThumbsDown,
  ChevronUp,
  Edit2,
  Send,
  Loader2,
  Sparkles,
  RefreshCw
} from 'lucide-react';

// Helper to strip raw HTML tags, decode HTML entities & format email text cleanly
function cleanBodyContent(content: string): string {
  if (!content) return '';
  
  // Convert line breaks and paragraph breaks before stripping tags
  let text = content
    .replace(/<br\s*[\/]?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/tr>/gi, '\n')
    .replace(/<\/li>/gi, '\n');
    
  // Strip all remaining HTML tags
  text = text.replace(/<[^>]+>/g, '');

  // Decode common HTML entities
  text = text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&#x2F;/gi, '/');

  // Normalize line breaks & excessive space
  return text
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export default function EmailDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [isEditing, setIsEditing] = useState(false);
  const [editedResponse, setEditedResponse] = useState('');
  const [orderInfoExpanded, setOrderInfoExpanded] = useState(true);
  const [draftCollapsed, setDraftCollapsed] = useState(false);
  const [userFeedback, setUserFeedback] = useState<'upvote' | 'downvote' | null>(null);
  const [showRegenerateBox, setShowRegenerateBox] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');

  const presetPrompts = [
    "Make response more polite & empathetic",
    "Offer a replacement or 10% discount",
    "Ask customer to provide photos of damaged item",
    "Keep response short, clear & direct",
    "Translate response to English"
  ];

  // Fetch 100% real email detail from PostgreSQL backend API
  const { data: email, isLoading, isError } = useQuery({
    queryKey: ['emailDetail', id],
    queryFn: async () => {
      const response = await reviewAPI.getById(id!);
      const data = response.data;
      const entities = data.extracted_entities || {};
      const exOrder = data.electronicx_order || {};
      const buyerName = exOrder.customer_name || entities.customer_name || entities.buyer_name || (data.from_address ? data.from_address.split('@')[0] : 'Customer');
      const platform = entities.platform || data.platform || 'unknown';
      const jtlData = entities.jtl_order_data || {};
      return {
        id: data.id,
        customer_name: buyerName,
        from_address: data.from_address,
        channel: platform === 'amazon' ? 'Amazon' : (platform === 'ebay' ? 'eBay' : 'eDesk - Website'),
        country_flag: data.from_address?.endsWith('.de') ? '🇩🇪' : (data.from_address?.endsWith('.ie') ? '🇮🇪' : '🇬🇧'),
        subject: data.subject || `Inquiry from ${buyerName}`,
        body: data.body || '',
        category: data.category || 'general',
        created_at: new Date(data.created_at || Date.now()).toLocaleTimeString('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' }) + ' CET',
        ticket_number: data.id.substring(0, 6),
        response_text: data.response_text || 'AI Response processing...',
        response_status: data.response_status,
        // Order info — electronicx DB (always on) takes priority, then extracted entities, then JTL
        order_id: exOrder.order_number || entities.order_id || entities.extracted_order_id || null,
        product_name: exOrder.product_name || jtlData.product_name || entities.product_name || null,
        asin: jtlData.asin || entities.asin || null,
        buyer_email: exOrder.customer_email || jtlData.buyer_email || entities.buyer_email || data.from_address || null,
        order_status: exOrder.order_status || jtlData.order_status || entities.order_status || null,
        tracking_number: exOrder.tracking_number || jtlData.tracking_number || entities.tracking_number || null,
        carrier: exOrder.carrier || jtlData.carrier || (entities.tracking_number ? 'DHL Paket' : null),
        sku: exOrder.sku || jtlData.sku || entities.sku || null,
        order_total: exOrder.total ? `${exOrder.total} ${exOrder.currency || 'EUR'}` : '49.99 EUR',
        jtl_order_id: exOrder.jtl_order_id || jtlData.internal_order_number || null,
        quantity: jtlData.quantity || 1,
        fulfillment_type: jtlData.is_fba ? 'FBA (Amazon)' : 'Merchant (FBM)',
        order_date: jtlData.order_date || 'Recent',
        estimated_delivery: jtlData.estimated_delivery || '1-2 business days',
        shipping_destination: exOrder.shipping_city ? `${exOrder.shipping_city}, ${exOrder.shipping_country || 'DE'}` : 'Germany',
        return_eligible: 'Eligible (30-day Window)',
      };
    },
    enabled: !!id,
    refetchInterval: 3000,
  });

  // Approve & Send Mutation
  const sendMutation = useMutation({
    mutationFn: async () => {
      const res = await reviewAPI.approveAndSend(id!, 'Admin');
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || 'Response sent to customer!');
      queryClient.invalidateQueries({ queryKey: ['reviewQueue'] });
      queryClient.invalidateQueries({ queryKey: ['reviewStats'] });
      navigate('/emails');
    },
    onError: () => {
      toast.error('Failed to send response');
    },
  });

  // Edit Response Mutation
  const updateMutation = useMutation({
    mutationFn: async (newText: string) => {
      const res = await reviewAPI.updateResponse(id!, newText);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Response updated');
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['emailDetail', id] });
    },
  });

  // AI Feedback Mutation (Upvote / Downvote)
  const feedbackMutation = useMutation({
    mutationFn: async (type: 'upvote' | 'downvote') => {
      await feedbackAPI.submit(id!, type);
      setUserFeedback(type);
    },
    onSuccess: () => {
      toast.success('Thank you for your feedback!');
    },
  });

  // Regenerate AI Response Mutation
  const regenerateMutation = useMutation({
    mutationFn: async (promptText?: string) => {
      const res = await reviewAPI.regenerate(id!, promptText);
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || 'New AI response generated successfully!');
      setShowRegenerateBox(false);
      setCustomPrompt('');
      queryClient.invalidateQueries({ queryKey: ['emailDetail', id] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to regenerate AI response');
    },
  });

  const handleCopyText = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleSend = () => {
    sendMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-white h-full">
        <div className="flex items-center space-x-2 text-slate-500 text-sm">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          <span>Loading ticket details from database...</span>
        </div>
      </div>
    );
  }

  if (isError || !email) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-white h-full text-slate-500">
        <p className="text-base font-semibold text-slate-700 mb-2">Ticket not found</p>
        <button
          onClick={() => navigate('/emails')}
          className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded font-medium hover:bg-blue-700"
        >
          Return to Mailbox
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white select-none overflow-hidden">
      {/* Main Ticket Workspace & Right Sidebar */}
      <div className="flex-1 flex overflow-hidden">
        {/* Main Workspace (Middle Area) */}
        <div className="flex-1 flex flex-col overflow-y-auto bg-slate-50/50 p-6">
          {/* Ticket Toolbar Pills & Action Icons */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <button className="bg-white border border-slate-200 rounded-md px-3 py-1 text-xs font-semibold text-slate-700 flex items-center space-x-1.5 shadow-xs hover:bg-slate-50">
                <Mail className="w-3.5 h-3.5 text-slate-500" />
                <span>{email.response_status || 'New ticket'}</span>
                <ChevronDown className="w-3 h-3 text-slate-400" />
              </button>

              <button className="bg-white border border-slate-200 rounded-md px-3 py-1 text-xs font-semibold text-slate-700 flex items-center space-x-1.5 shadow-xs hover:bg-slate-50">
                <span>{email.category}</span>
                <ChevronDown className="w-3 h-3 text-slate-400" />
              </button>
            </div>
          </div>

          {/* Ticket Meta Line */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] font-medium text-slate-500">
                Ticket #{email.ticket_number}
              </div>
              <h1 className="text-lg font-bold text-slate-900 tracking-tight">
                {email.subject}
              </h1>
            </div>

            <div className="flex items-center space-x-2">
              <span className="bg-emerald-50 text-emerald-700 border border-emerald-200/80 text-[11px] font-semibold px-2 py-0.5 rounded flex items-center space-x-1">
                <Mail className="w-3 h-3 text-emerald-600" />
                <span>Read</span>
              </span>
            </div>
          </div>

          {/* Customer Message Card */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-xs mb-4">
            <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-2">
              <div className="flex items-center space-x-2">
                <div className="w-7 h-7 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500">
                  <Mail className="w-3.5 h-3.5" />
                </div>
                <span className="font-semibold text-xs text-slate-900">{email.customer_name}</span>
              </div>
              <div className="flex items-center space-x-2 text-xs text-slate-500 font-medium">
                <span>{email.created_at}</span>
                <button className="p-1 text-slate-400 hover:text-slate-700">
                  <MoreVertical className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="text-sm text-slate-800 leading-relaxed font-sans whitespace-pre-line break-words select-text">
              {cleanBodyContent(email.body)}
            </div>
          </div>

          {/* AI Response / Draft Container */}
          <div className="bg-[#F0F6FF] border border-blue-100 rounded-xl p-4 shadow-xs mt-2 relative">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-bold text-blue-900">Reply</span>
                <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider border border-blue-200 bg-blue-50 px-1.5 py-0.2 rounded">
                  DRAFT
                </span>
              </div>
              <button
                onClick={() => setDraftCollapsed(!draftCollapsed)}
                className="text-slate-400 hover:text-slate-700 p-1"
              >
                {draftCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
              </button>
            </div>

            {!draftCollapsed && (
              <>
                <div className="bg-white border border-slate-200/80 rounded-lg p-5 shadow-xs mb-3 relative">
                  <div className="absolute top-4 right-4 flex items-center space-x-2">
                    <button
                      onClick={() => feedbackMutation.mutate('upvote')}
                      className={`p-1 rounded transition-colors ${
                        userFeedback === 'upvote' ? 'text-blue-600 bg-blue-50' : 'text-slate-400 hover:text-slate-700'
                      }`}
                      title="Helpful AI response"
                    >
                      <ThumbsUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => feedbackMutation.mutate('downvote')}
                      className={`p-1 rounded transition-colors ${
                        userFeedback === 'downvote' ? 'text-red-600 bg-red-50' : 'text-slate-400 hover:text-slate-700'
                      }`}
                      title="Unhelpful AI response"
                    >
                      <ThumbsDown className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setShowRegenerateBox(!showRegenerateBox);
                        if (isEditing) setIsEditing(false);
                      }}
                      className={`px-2 py-1 rounded transition-colors flex items-center space-x-1 border text-xs font-semibold ${
                        showRegenerateBox
                          ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:text-indigo-600 hover:border-indigo-200'
                      }`}
                      title="Regenerate response with AI instructions"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                      <span>Regenerate</span>
                    </button>
                    <button
                      onClick={() => {
                        setIsEditing(!isEditing);
                        setEditedResponse(email.response_text);
                        if (showRegenerateBox) setShowRegenerateBox(false);
                      }}
                      className="p-1 text-slate-400 hover:text-slate-700 rounded"
                      title="Edit response"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>

                  {isEditing ? (
                    <div className="space-y-3 pt-4">
                      <textarea
                        value={editedResponse}
                        onChange={(e) => setEditedResponse(e.target.value)}
                        rows={6}
                        className="w-full border border-slate-200 rounded-md p-3 text-xs text-slate-800 focus:outline-none focus:border-blue-500 font-sans"
                      />
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => setIsEditing(false)}
                          className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => updateMutation.mutate(editedResponse)}
                          className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded font-medium hover:bg-blue-700"
                        >
                          Save Draft
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-slate-800 leading-relaxed whitespace-pre-wrap font-normal pr-36 pt-1">
                      {email.response_text}
                    </div>
                  )}

                  {/* Regenerate with Custom Prompt Box */}
                  {showRegenerateBox && (
                    <div className="mt-4 pt-3.5 border-t border-slate-100 bg-indigo-50/40 p-3.5 rounded-lg border border-indigo-100/80 transition-all">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-1.5 text-xs font-bold text-indigo-950">
                          <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                          <span>Custom AI Prompt / Instructions</span>
                        </div>
                        <span className="text-[10px] text-slate-500 font-normal">Optional instruction for AI</span>
                      </div>

                      {/* Preset prompt quick chips */}
                      <div className="flex flex-wrap gap-1.5 mb-2.5">
                        {presetPrompts.map((preset, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setCustomPrompt(preset)}
                            className={`text-[10px] px-2 py-0.5 rounded-full border transition-all ${
                              customPrompt === preset
                                ? 'bg-indigo-600 text-white border-indigo-600 font-medium'
                                : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50'
                            }`}
                          >
                            ⚡ {preset}
                          </button>
                        ))}
                      </div>

                      {/* Custom Prompt Input */}
                      <textarea
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        placeholder="Type custom instructions for AI (e.g. 'Tell customer package was delayed by DHL, offer 10% refund')..."
                        rows={2}
                        className="w-full border border-indigo-200 rounded-md p-2.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-sans bg-white"
                      />

                      <div className="flex items-center justify-between mt-2.5">
                        <button
                          onClick={() => {
                            setShowRegenerateBox(false);
                            setCustomPrompt('');
                          }}
                          className="px-2.5 py-1 text-xs text-slate-500 hover:text-slate-800"
                        >
                          Cancel
                        </button>

                        <button
                          onClick={() => regenerateMutation.mutate(customPrompt || undefined)}
                          disabled={regenerateMutation.isPending}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-4 py-1.5 rounded-md shadow-xs transition-colors flex items-center space-x-1.5 disabled:opacity-50 cursor-pointer"
                        >
                          {regenerateMutation.isPending ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              <span>Generating response...</span>
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-3.5 h-3.5" />
                              <span>{customPrompt ? 'Generate with Custom Prompt' : 'Regenerate Answer'}</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end pt-1">
                  <button
                    onClick={handleSend}
                    disabled={sendMutation.isPending}
                    className="bg-[#2563EB] hover:bg-blue-700 text-white text-xs font-semibold px-5 py-2 rounded-md shadow-sm transition-colors flex items-center space-x-1.5 cursor-pointer disabled:opacity-50"
                  >
                    <Send className="w-3.5 h-3.5" />
                    <span>Send</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right Customer & Order Info Sidebar */}
        <aside className="w-[280px] bg-white border-l border-slate-200 flex flex-col p-4 shrink-0 overflow-y-auto">
          <div className="mb-6 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-bold text-slate-900 text-sm">{email.customer_name}</span>
              <button
                onClick={() => handleCopyText(email.customer_name)}
                className="text-slate-400 hover:text-slate-700"
                title="Copy name"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="flex items-center justify-between text-xs text-slate-600 border-b border-slate-100 pb-3">
              <span className="truncate pr-2">{email.from_address}</span>
              <button
                onClick={() => handleCopyText(email.from_address)}
                className="text-slate-400 hover:text-slate-700 shrink-0"
                title="Copy email"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="flex items-center justify-between pt-1 text-xs text-slate-600">
              <span className="font-medium text-slate-500">Channel</span>
              <div className="flex items-center space-x-1">
                <span>{email.country_flag}</span>
                <span className="font-semibold text-slate-800">{email.channel}</span>
                <button
                  onClick={() => handleCopyText(email.channel)}
                  className="text-slate-400 hover:text-slate-700 ml-1"
                >
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <div
              onClick={() => setOrderInfoExpanded(!orderInfoExpanded)}
              className="flex items-center justify-between font-bold text-xs text-slate-700 tracking-wider uppercase cursor-pointer hover:text-slate-900 mb-3"
            >
              <span>ORDER INFO</span>
              <ChevronDown
                className={`w-4 h-4 text-slate-400 transition-transform ${
                  orderInfoExpanded ? 'rotate-180' : ''
                }`}
              />
            </div>

            {orderInfoExpanded && (
              <div className="space-y-2">
                {email.order_id ? (
                  <div className="space-y-2">
                    {/* Order ID */}
                    <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Order ID</span>
                      <span className="text-xs font-semibold text-slate-800 font-mono">{email.order_id}</span>
                    </div>
                    {/* Product */}
                    {email.product_name && (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide block mb-0.5">Product</span>
                        <span className="text-xs text-slate-800 leading-snug">{email.product_name}</span>
                      </div>
                    )}
                    {/* ASIN / SKU */}
                    {(email.asin || email.sku) && (
                      <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">{email.asin ? 'ASIN' : 'SKU'}</span>
                        <span className="text-xs font-mono text-slate-700">{email.asin || email.sku}</span>
                      </div>
                    )}
                    {/* Order Status */}
                    {email.order_status && (
                      <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Status</span>
                        <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">{email.order_status}</span>
                      </div>
                    )}
                    {/* Tracking */}
                    {email.tracking_number && (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide block mb-0.5">Tracking</span>
                        <span className="text-xs font-mono text-blue-700">{email.tracking_number}</span>
                      </div>
                    )}
                    {/* Fulfillment Type */}
                    {email.fulfillment_type && (
                      <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Fulfillment</span>
                        <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded">{email.fulfillment_type}</span>
                      </div>
                    )}
                    {/* Carrier */}
                    {email.carrier && (
                      <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Carrier</span>
                        <span className="text-xs font-medium text-slate-800">{email.carrier}</span>
                      </div>
                    )}
                    {/* Estimated Delivery */}
                    {email.estimated_delivery && (
                      <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Est. Delivery</span>
                        <span className="text-xs font-semibold text-amber-700">{email.estimated_delivery}</span>
                      </div>
                    )}
                    {/* Destination */}
                    {email.shipping_destination && (
                      <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Destination</span>
                        <span className="text-xs font-medium text-slate-700">{email.shipping_destination}</span>
                      </div>
                    )}
                    {/* Return Eligible */}
                    {email.return_eligible && (
                      <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Returns</span>
                        <span className="text-[11px] font-semibold text-emerald-700">{email.return_eligible}</span>
                      </div>
                    )}
                    {/* Buyer Email */}
                    {email.buyer_email && email.buyer_email !== email.from_address && (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide block mb-0.5">Buyer Email</span>
                        <span className="text-xs text-slate-700 truncate block">{email.buyer_email}</span>
                      </div>
                    )}
                    {/* Order Total */}
                    {email.order_total && (
                      <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Total</span>
                        <span className="text-xs font-semibold text-slate-800">{email.order_total}</span>
                      </div>
                    )}
                    {/* JTL Order ID */}
                    {email.jtl_order_id && (
                      <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">JTL Ref</span>
                        <span className="text-xs font-mono text-slate-700">{email.jtl_order_id}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-[#F0F7FF] border border-blue-100 rounded-lg p-3 text-[11px] text-slate-700 leading-relaxed">
                    <span className="font-semibold text-slate-900">No order linked</span>
                    <p className="mt-1 text-slate-500">Order info will appear here once an order ID is extracted from this email.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Internal Ticket Notes Section */}
          <div className="border-t border-slate-200 pt-4 mt-4">
            <CommentSection emailId={email.id} />
          </div>
        </aside>
      </div>
    </div>
  );
}
